<template>
  <div class="min-h-screen bg-black text-white">
    <!-- Sidebar -->
    <div 
      class="fixed inset-y-0 left-0 z-50 w-64 bg-black/95 backdrop-blur-sm transform transition-transform duration-300 sidebar"
      :class="{ '-translate-x-full': !isSidebarOpen, 'translate-x-0': isSidebarOpen }"
    >
      <div class="h-full flex flex-col">
        <!-- Logo -->
        <div class="p-6">
          <img src="/icon.png" alt="Ploppin Logo" class="w-12 h-12 rounded-2xl" />
        </div>

        <!-- Navigation -->
        <nav class="flex-1 px-4 space-y-2">
          <NuxtLink 
            v-for="item in navItems" 
            :key="item.path"
            :to="item.path"
            class="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
            :class="{ 'bg-white/10 text-white': route.path === item.path }"
          >
            <component :is="item.icon" class="w-5 h-5" />
            <span>{{ item.name }}</span>
          </NuxtLink>
        </nav>

        <!-- Credits -->
        <div class="p-4 border-t border-white/10">
          <div class="flex items-center gap-2 text-gray-300">
            <Gem class="w-5 h-5 text-white" />
            <span>{{ credits }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="min-h-screen">
      <!-- Top Bar -->
      <header class="fixed top-0 left-0 right-0 z-40 bg-black/50 backdrop-blur-sm border-b border-white/10">
        <div class="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <!-- Left: Menu Button & Credits -->
          <div class="flex items-center gap-4">
            <button 
              @click="toggleSidebar"
              class="p-2 hover:bg-white/5 rounded-lg transition-colors"
            >
              <Menu class="w-6 h-6" />
            </button>
            <div class="flex items-center gap-2 text-sm">
              <Gem class="w-5 h-5 text-white" />
              <span>{{ credits }}</span>
            </div>
          </div>

          <!-- Right: Profile Menu -->
          <ProfileMenu 
            :is-profile-menu-open="isProfileMenuOpen"
            @toggle-profile-menu="toggleProfileMenu"
            @close-profile-menu="closeProfileMenu"
          />
        </div>
      </header>

      <!-- Page Content -->
      <main class="pt-16">
        <slot />
      </main>
    </div>

    <!-- Overlay -->
    <div 
      v-if="isSidebarOpen"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
      @click="toggleSidebar"
    ></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useAuth } from '~/composables/useAuth'
import { useRoute } from 'vue-router'
import { 
  Gem, 
  Menu, 
  Home,
  Image,
  StarsIcon,
  LogOut
} from 'lucide-vue-next'
import ProfileMenu from '~/components/ProfileMenu.vue'

const { credits } = useAuth()
const route = useRoute()

// State
const isSidebarOpen = ref(false)
const isProfileMenuOpen = ref(false)

// Navigation items with lucide icons
const navItems = [
  { 
    name: 'Home', 
    path: '/app', 
    icon: Home
  },
  { 
    name: 'Generations', 
    path: '/app/generations', 
    icon: Image
  },
  { 
    name: 'Premium', 
    path: '/?section=pricing', 
    icon: StarsIcon
  },
  { name: 'Exit', path: '/', icon: LogOut }
]

// Methods
const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleProfileMenu = () => {
  isProfileMenuOpen.value = !isProfileMenuOpen.value
}

const closeProfileMenu = () => {
  isProfileMenuOpen.value = false
}

// Close menus on click outside
onMounted(() => {
  const handleClickOutside = (e) => {
    // Close profile menu if clicking outside
    const profileMenu = e.target.closest('.profile-menu')
    if (!profileMenu && isProfileMenuOpen.value) {
      isProfileMenuOpen.value = false
    }
    
    // Close sidebar when clicking outside
    if (isSidebarOpen.value) {
      const sidebar = e.target.closest('.sidebar')
      const menuButton = e.target.closest('button')
      if (!sidebar && !menuButton) {
        isSidebarOpen.value = false
      }
    }
  }
  
  document.addEventListener('click', handleClickOutside)
  
  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })
})

// Close sidebar on route change
watch(() => route.path, () => {
  isSidebarOpen.value = false
  isProfileMenuOpen.value = false
})
</script>